import { useEffect, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { CollapsibleContent } from 'components/Collapsible';
import { ExpandCollapse, SkeletonBlurb } from 'components/Containers';
import DischargeModal from 'components/modals/DischargeModal';
import EscalateModal from 'components/modals/EscalateModal';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import Tabs from 'components/Tabs';
import { TabType } from 'components/Tabs';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useIsMobile from 'hooks/useIsMobile';
import { simpleDate } from 'lib/date';
import { capitalizeGender } from 'lib/util';
import { EpisodeAccessLevel } from 'models/Episode';
import { ManagerGroupTypeConfig } from 'models/GroupType';
import { PERMISSIONS } from 'models/Profile';
import { indexAttachments } from 'services/api/attachments';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, H1, LabelBold } from 'styles/typography';
import DischargedIcon from 'svg/DischargedIcon';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import NoteIcon from 'svg/NoteIcon';
import WarnIcon from 'svg/WarnIcon';

import ArchivedWarningBanner from './blurbs/ArchivedWarningBanner';
import PatientClassifications from './blurbs/patientOverview/PatientClassifications';
import { ProgressBarComponent } from './charts/PatientProgressBar';
import LinkedRecordsBanner from './LinkedRecordsBanner';
import LinkedRecordsDropdown from './LinkedRecordsDropdown';
import PatientActionsMenu from './PatientActionsMenu';

const PatientHeader = () => {
  const [expanded, setExpanded] = useState(false);
  const [showDischarge, setShowDischarge] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);

  const { episode } = useEpisode();
  const { locationEpisode, selectedLocationEpisodeId, loading: locationEpisodeLoading } = useSelectedLocationEpisode();

  const { profile } = useProfile();

  const invalidateQueries = useInvalidateQueries();

  const isMobile = useIsMobile();
  const { setNavigationOpen } = useMobileNavigationActions();

  const navigate = useNavigate();

  const { translate } = useTranslation();

  const isLatestActiveStay = locationEpisode?.latest && !locationEpisode.archived && episode && !episode?.archived;
  const showDischargeButton = isLatestActiveStay && !locationEpisode.currentRehabState.queue;
  const canDischargeState =
    episode?.currentRehabState && !episode?.currentRehabState?.discharged && !episode?.currentRehabState?.queue;
  const showEscalateButton =
    isLatestActiveStay &&
    ((locationEpisode?.owner?.groupType?.config as ManagerGroupTypeConfig)?.canReceiveEscalations ?? true);

  const name = episode?.patient.name;
  const dob = episode?.patient.dateOfBirth;
  const episodeDate = episode?.admittedOn;
  const gender = episode?.patient.sex ?? 'unknown';
  const formattedGender = capitalizeGender(gender);
  const externalId = episode?.patient.externalId;

  const currentGroupTypeClientConfig = locationEpisode?.owner?.client?.configForManagedGroupType(
    locationEpisode?.group?.groupTypeId
  );

  const referral = locationEpisode?.latestReferralReview;

  const showPatientStoryTab = useMemo(() => {
    return episode && episode?.currentUserAccessLevel !== EpisodeAccessLevel.RESTRICTED;
  }, [episode]);

  const showFilesTab = useMemo(() => {
    if (!episode) return false;

    return (episode && episode?.currentUserAccessLevel !== EpisodeAccessLevel.RESTRICTED) || !!referral;
  }, [episode, referral]);

  const showReferralTab = useMemo(() => {
    if (referral) return true;

    //if the patient isn't in a state where a referral can be added, and there isn't one, there's no reason to show the referral tab
    return (
      showPatientStoryTab &&
      showFilesTab &&
      locationEpisode?.inTreatment &&
      !episode?.archived &&
      !locationEpisode?.archived &&
      profile.has(PERMISSIONS.reviewReferralCreate, { episode, locationEpisode }) &&
      (profile.isManagerFor(episode) || profile.isProviderFor(locationEpisode.group.id)) &&
      !!currentGroupTypeClientConfig?.referral
    );
  }, [
    showPatientStoryTab,
    showFilesTab,
    locationEpisode,
    episode,
    profile,
    currentGroupTypeClientConfig?.referral,
    referral,
  ]);

  const showConcurrentReviewTab =
    episode && locationEpisode && profile.isManagerFor(episode) && locationEpisode.umAISummaryEnabled;

  const invalidateData = () => {
    invalidateQueries({
      locationEpisode: { id: locationEpisode?.id },
      attachmentsCount: { locationEpisodeId: selectedLocationEpisodeId },
      referral: { id: referral?.id },
    });
  };

  const patientStoryTab: TabType = useMemo(() => ({ label: 'Patient Story', value: '' }), []);

  const { data: attachmentsCount } = useQuery({
    queryKey: attachmentsQueryKeys.count({ archived: false, locationEpisodeId: locationEpisode?.id }),
    queryFn: ({ signal }) =>
      indexAttachments(
        { archived: false, locationEpisodeId: locationEpisode?.id, pageSize: 1, page: 1, viewOnly: true },
        signal
      ),
    select: (data) => data?.meta.totalRecords,
    enabled: !!locationEpisode?.id,
  });

  const filesTab = useMemo(
    () => ({ label: `Files (${attachmentsCount ?? '-'})`, value: '/files', 'data-gtm-id': 'patientFiles' }) as TabType,
    [attachmentsCount]
  );

  const referralTab = useMemo(
    () => ({
      label: 'Referral',
      value: '/referrals',
      affix: referral?.hasNewChanges && !locationEpisodeLoading && (
        <NoteIcon color={colors.primaryBlue} width={12} height={12} />
      ),
    }),
    [referral, locationEpisodeLoading]
  );

  const concurrentReviewTab = useMemo(
    () => ({
      label: 'Concurrent Reviews',
      value: '/concurrent-reviews',
      'data-cy': 'ConcurrentReviewsTab',
    }),
    []
  );

  const tabs = useMemo(
    () => [
      ...(showPatientStoryTab ? [patientStoryTab] : []),
      ...(showReferralTab ? [referralTab] : []),
      ...(showConcurrentReviewTab ? [concurrentReviewTab] : []),
      ...(showFilesTab ? [filesTab] : []),
    ],
    [
      patientStoryTab,
      filesTab,
      showReferralTab,
      showPatientStoryTab,
      showFilesTab,
      referralTab,
      concurrentReviewTab,
      showConcurrentReviewTab,
    ]
  );

  const referralBaseMatch = useMatch({ path: '/patients/:episodeId/referrals/*' });
  const filesRouteMatch = useMatch({ path: '/patients/:episodeId/files' });
  const concurrentReviewRouteMatch = useMatch({ path: '/patients/:episodeId/concurrent-reviews/*' });

  const currentTab = useMemo(() => {
    if (referralBaseMatch) {
      return referralTab;
    } else if (filesRouteMatch) {
      return filesTab;
    } else if (concurrentReviewRouteMatch) {
      return concurrentReviewTab;
    } else {
      return patientStoryTab;
    }
  }, [
    referralBaseMatch,
    filesRouteMatch,
    concurrentReviewRouteMatch,
    referralTab,
    filesTab,
    concurrentReviewTab,
    patientStoryTab,
  ]);

  // Switch to patient story tab if we're on referral tab but it shouldn't be shown after loading
  useEffect(() => {
    if (locationEpisode && currentTab?.value.includes('referral') && !showReferralTab) {
      navigate(`/patients/${episode?.id}`);
    }
  }, [locationEpisode, showReferralTab, currentTab?.value, navigate, episode?.id]);

  // Switch to patient story tab if we're on concurrent review tab but it shouldn't be shown after loading
  useEffect(() => {
    if (locationEpisode && currentTab?.value.includes('concurrent-reviews') && !showConcurrentReviewTab) {
      navigate(`/patients/${episode?.id}`);
    }
  }, [locationEpisode, showConcurrentReviewTab, currentTab?.value, navigate, episode?.id]);

  const handleTabClick = (tab: TabType) => {
    navigate(`/patients/${episode?.id}${tab.value}`);
  };

  return (
    <Header id='patient-header'>
      {episode?.archived && <ArchivedWarningBanner episode={episode} />}
      <LinkedRecordsBanner />
      <TopContent id='top-content'>
        <TopContentRow>
          <ContentColumn>
            <ContentRow id='content-row'>
              <InnerContentRow id='inner-content-row'>
                {!name && (
                  <>
                    <SkeletonBlank $width={200} />
                    <SkeletonBlank $width={80} />
                    <SkeletonBlank $width={100} />
                  </>
                )}
                {isMobile && (
                  <Button variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
                    <HamburgerMenuIcon />
                  </Button>
                )}
                <StyledPatientHeader id='styled-patient-header'>{name}</StyledPatientHeader>
                {!isMobile && profile.canActAsManager && externalId && <Dob>ID: {externalId}</Dob>}
                {!isMobile && dob && <Dob>DOB: {simpleDate(dob)}</Dob>}
                <ExpandContainer>
                  <ExpandCollapse onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Collapse' : 'Expand'}
                  </ExpandCollapse>
                </ExpandContainer>
              </InnerContentRow>
            </ContentRow>
            <LinkedRecordsDropdown />
            <ContentRow>{locationEpisode && <PatientClassifications locationEpisode={locationEpisode} />}</ContentRow>
            {locationEpisode && isMobile && (
              <TopContentRow>
                <StyledCollapsibleContent collapsed={!expanded}>
                  <DataRow>
                    <LabelBold>
                      <span className='info-key'>Hospital Admission:</span>
                      <span>{simpleDate(episodeDate) || '—'}</span>
                    </LabelBold>

                    <LabelBold>
                      <span className='info-key'>Gender:</span>
                      <span>{formattedGender}</span>
                    </LabelBold>
                  </DataRow>
                  <ProgressBarComponent locationEpisode={locationEpisode} />
                </StyledCollapsibleContent>
              </TopContentRow>
            )}
          </ContentColumn>
          <ContentColumn>
            <ContentActionsRow id='contents-action-row'>
              <InnerContentRow>
                <ButtonGroup fullWidth style={{ justifyContent: 'flex-end' }}>
                  {showDischargeButton && (
                    <AccessGuard
                      accessor={PERMISSIONS.rehabStateDischargeCreate}
                      locationEpisode={locationEpisode}
                      episode={episode}>
                      <Button
                        fullWidth
                        variant='outline-primary'
                        onClick={() => setShowDischarge(true)}
                        disabled={!canDischargeState}>
                        {!isMobile && <DischargedIcon width={18} height={18} color={colors.primaryBlue} />}
                        {translate('Discharge')}
                      </Button>
                    </AccessGuard>
                  )}
                  {showEscalateButton && (
                    <AccessGuard
                      accessor={PERMISSIONS.escalationCreate}
                      locationEpisode={locationEpisode}
                      episode={episode}>
                      <Button fullWidth variant='outline-danger' onClick={() => setShowEscalate(true)}>
                        {!isMobile && <WarnIcon width={18} height={18} color={colors.accentRed} />}
                        Escalate
                      </Button>
                    </AccessGuard>
                  )}
                  {!!episode && !!locationEpisode && (
                    <PatientActionsMenu episode={episode} invalidateData={invalidateData} />
                  )}
                </ButtonGroup>
                {showDischarge && (
                  <DischargeModal
                    episode={episode!}
                    locationEpisode={locationEpisode!}
                    setShow={setShowDischarge}
                    patientName={name!}
                    invalidateData={invalidateData}
                  />
                )}
                {showEscalate && (
                  <EscalateModal locationEpisode={locationEpisode!} setShow={setShowEscalate} patientName={name!} />
                )}
              </InnerContentRow>
            </ContentActionsRow>
          </ContentColumn>
        </TopContentRow>

        {locationEpisode && !isMobile && (
          <TopContentRow>
            <StyledCollapsibleContent collapsed={!expanded}>
              <DataRow>
                <LabelBold>
                  <span className='info-key'>Hospital Admission:</span>
                  <span>{simpleDate(episodeDate) || '—'}</span>
                </LabelBold>

                <LabelBold>
                  <span className='info-key'>Gender:</span>
                  <span>{formattedGender}</span>
                </LabelBold>
              </DataRow>
              <ProgressBarComponent locationEpisode={locationEpisode} />
            </StyledCollapsibleContent>
          </TopContentRow>
        )}
      </TopContent>
      <BottomContent>
        <Tabs tabs={tabs} onTabClick={handleTabClick} selectedTab={currentTab} />
      </BottomContent>
    </Header>
  );
};

export default PatientHeader;

const MAX_CONTENT_WIDTH = 1072;

const Dob = styled(BodySmall)`
  color: var(--black-75);
  padding-top: 4px;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex-shrink: 0;
`;

const Header = styled.div`
  background-color: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const TopContent = styled.div`
  padding-left: 24px;
  padding-top: 16px;
  padding-bottom: 16px;
  max-width: 1152px;

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 24px 24px 24px 56px;
  }
`;

const TopContentRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  @media ${({ theme }) => theme.devices.desktop} {
    flex-direction: row;
  }
`;

const BottomContent = styled.div`
  padding-left: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 0px 24px 0px 56px;
  }
`;
const ExpandContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  @media not ${({ theme }) => theme.devices.desktop} {
    justify-content: flex-end;
    padding-right: 24px;
    overflow: visible;
  }
`;
const InnerContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 4px 0px;
`;
const ContentActionsRow = styled.div`
  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    align-items: center;
    padding-right: 0px;
  }
  padding-right: 25px;
`;

const ContentRow = styled.div`
  min-width: 0;
  gap: 6px;
  overflow: hidden;
  flex-direction: column;
  display: flex;
  max-width: ${MAX_CONTENT_WIDTH}px;

  @media ${({ theme }) => theme.devices.desktop} {
    flex-direction: row;
  }

  .icon {
    flex-shrink: 0;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const DataRow = styled.div`
  margin-top: 16px;
  display: flex;
  height: 19px;
  gap: 24px;

  .info-key {
    color: var(--black-50);
    margin-right: 4px;
  }
`;

const StyledPatientHeader = styled(H1)`
  font-weight: var(--font-weight-extra-bold);
  font-size: 20px;
  @media ${({ theme }) => theme.devices.desktop} {
    font-size: 24px;
  }
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SkeletonBlank = styled(SkeletonBlurb)<{ $width: number }>`
  border-radius: 17px;
  height: 17px;
  width: ${({ $width }) => $width + 'px'};
`;

const StyledCollapsibleContent = styled(CollapsibleContent)`
  width: 100%;
`;
